import React, { useEffect, useMemo, useState } from 'react';
import { classesApi, gradesApi, gradingApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { ChalkboardTeacher, FloppyDisk, Trophy, ArrowsClockwise, ChartLine, FilePdf, FilesIcon, Lock, LockOpen, Calculator, Plus, Trash, Calendar, TrendUp, TrendDown, ArrowRight } from '@phosphor-icons/react';
import { toast } from 'sonner';

const PERIOD_OPTIONS = {
  trimestre: [
    { value: 'trimestre-1', label: '1er trimestre' },
    { value: 'trimestre-2', label: '2è trimestre' },
    { value: 'trimestre-3', label: '3è trimestre' },
  ],
  semestre: [
    { value: 'semestre-1', label: '1er semestre' },
    { value: 'semestre-2', label: '2è semestre' },
  ],
  periode: [
    { value: 'periode-1', label: '1ère période' },
    { value: 'periode-2', label: '2è période' },
    { value: 'periode-3', label: '3è période' },
    { value: 'periode-4', label: '4è période' },
    { value: 'periode-5', label: '5è période' },
    { value: 'periode-6', label: '6è période' },
    { value: 'examen-1', label: '1er examen' },
    { value: 'examen-2', label: '2è examen' },
    { value: 'examen-3', label: '3è examen' },
  ],
};

const SMILEY_LEVELS = [
  { level: 1, emoji: '😟', label: 'À renforcer', color: '#DC2626' },
  { level: 2, emoji: '😐', label: 'En cours', color: '#D97706' },
  { level: 3, emoji: '🙂', label: 'Acquis', color: '#16A34A' },
  { level: 4, emoji: '😄', label: 'Maîtrisé', color: '#0F9D58' },
];
const SMILEY_BY_LEVEL = Object.fromEntries(SMILEY_LEVELS.map(x => [x.level, x]));
const bilanFromLevels = (vals) => {
  const nums = (vals || []).map(v => parseInt(v, 10)).filter(n => n >= 1 && n <= 4);
  if (!nums.length) return null;
  return Math.max(1, Math.min(4, Math.round(nums.reduce((a, b) => a + b, 0) / nums.length)));
};

const APPRECIATION_BY_AVG = (avg) => {
  if (avg == null) return '—';
  if (avg >= 16) return 'Excellent';
  if (avg >= 14) return 'Très bien';
  if (avg >= 12) return 'Bien';
  if (avg >= 10) return 'Assez bien';
  if (avg >= 8) return 'Insuffisant';
  return 'Très insuffisant';
};

export default function Grades() {
  const { user } = useAuth();
  const [classes, setClasses] = useState([]);
  const [classConfigs, setClassConfigs] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [computed, setComputed] = useState(null);
  const [annual, setAnnual] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedSubject, setSelectedSubject] = useState('');
  const [grid, setGrid] = useState({});      // student_id → {sub_scores, sub_appreciations, appreciation, score, weighted_grades}
  const [saving, setSaving] = useState(false);
  const [wgDialog, setWgDialog] = useState({ open: false, studentId: null });
  const [progression, setProgression] = useState({ open: false, studentId: null, studentName: '', data: null, loading: false });

  // ----- Load progression when dialog opens -----
  useEffect(() => {
    if (!progression.open || !progression.studentId || progression.data) return;
    (async () => {
      try {
        const { data } = await gradesApi.progression(selectedClass, progression.studentId, computed?.school_year);
        setProgression(p => ({ ...p, data, loading: false }));
      } catch {
        toast.error('Impossible de charger la progression');
        setProgression(p => ({ ...p, loading: false }));
      }
    })();
  }, [progression.open, progression.studentId]); // eslint-disable-line

  // ----- Load classes + configs -----
  useEffect(() => { (async () => {
    try {
      const [a, b] = await Promise.all([
        classesApi.getAll(),
        gradingApi.classConfigs(),
      ]);
      setClasses(a.data);
      setClassConfigs(b.data);
    } catch (e) {
      toast.error('Erreur chargement');
    }
  })(); }, []);

  const classCfg = useMemo(
    () => classConfigs.find(c => c.class_name === selectedClass) || null,
    [classConfigs, selectedClass],
  );

  const periodOptions = useMemo(
    () => classCfg ? (PERIOD_OPTIONS[classCfg.period_type] || []) : [],
    [classCfg],
  );

  // Reset period & data when class changes
  useEffect(() => {
    setSelectedPeriod(''); setComputed(null); setSelectedSubject(''); setGrid({});
  }, [selectedClass]);

  // ----- Load computed when class+period set -----
  const loadComputed = async () => {
    if (!selectedClass || !selectedPeriod) return;
    setLoading(true);
    try {
      const { data } = await gradesApi.computed(selectedClass, selectedPeriod);
      setComputed(data);
      // Auto-pick first subject
      if (data.subjects?.length && !selectedSubject) {
        setSelectedSubject(data.subjects[0].name);
      }
      // Load annual stats in parallel (non-blocking)
      gradesApi.annual(selectedClass, data.school_year)
        .then((r) => setAnnual(r.data))
        .catch(() => setAnnual(null));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur chargement notes');
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (selectedClass && selectedPeriod) loadComputed();
  }, [selectedClass, selectedPeriod]);

  // ----- Build editable grid from computed when subject changes -----
  useEffect(() => {
    if (!computed || !selectedSubject) { setGrid({}); return; }
    const next = {};
    computed.students.forEach((st) => {
      const entry = st.subjects.find(e => e.subject === selectedSubject);
      next[st.student_id] = {
        sub_scores: entry?.sub_scores || {},
        sub_appreciations: entry?.sub_appreciations || {},
        sub_notes: entry?.sub_notes || {},
        appreciation: entry?.appreciation || '',
        score: entry?.score ?? null,
        weighted_grades: Array.isArray(entry?.weighted_grades) ? entry.weighted_grades : [],
      };
    });
    setGrid(next);
  }, [computed, selectedSubject]);

  const subjectMeta = useMemo(
    () => computed?.subjects?.find(s => s.name === selectedSubject) || null,
    [computed, selectedSubject],
  );

  const subActivities = useMemo(() => {
    const list = subjectMeta?.sub_activities || [];
    return list.map(sa => typeof sa === 'string' ? { name: sa, max_score: null } : sa);
  }, [subjectMeta]);

  const updateSub = (studentId, subName, value) => {
    const meta = subActivities.find(a => a.name === subName);
    let val = value === '' ? null : Number(value);
    if (val !== null && !Number.isNaN(val) && meta?.max_score != null && val > meta.max_score) {
      val = meta.max_score;
      toast.warning(`Note plafonnée au barème (${meta.max_score}) pour « ${subName} »`);
    }
    setGrid(prev => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        sub_scores: { ...(prev[studentId]?.sub_scores || {}), [subName]: val },
      },
    }));
  };

  const updateScore = (studentId, value) => {
    let val = value === '' ? null : Number(value);
    const cap = computed?.grading_mode === 'quantitative_total'
      ? (subjectMeta?.max_score ?? null) : 20;
    if (val !== null && !Number.isNaN(val) && cap != null && val > cap) {
      val = cap;
      toast.warning(`Note plafonnée au barème (${cap})`);
    }
    setGrid(prev => ({
      ...prev,
      [studentId]: { ...prev[studentId], score: val },
    }));
  };

  const updateSubNote = (studentId, subName, value) => {
    setGrid(prev => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        sub_notes: { ...(prev[studentId]?.sub_notes || {}), [subName]: value },
      },
    }));
  };

  const updateAppreciation = (studentId, value, subName = null) => {
    setGrid(prev => ({
      ...prev,
      [studentId]: subName
        ? { ...prev[studentId],
            sub_appreciations: { ...(prev[studentId]?.sub_appreciations || {}), [subName]: value } }
        : { ...prev[studentId], appreciation: value },
    }));
  };

  // --- Weighted grades helpers (secondaire only) ---
  const computeWeightedAverage = (list) => {
    const valid = (list || []).filter(w => w.score !== null && w.score !== '' && !Number.isNaN(Number(w.score)) && Number(w.coef) > 0);
    if (!valid.length) return null;
    const sSum = valid.reduce((acc, w) => acc + Number(w.score) * Number(w.coef), 0);
    const cSum = valid.reduce((acc, w) => acc + Number(w.coef), 0);
    return cSum > 0 ? Math.round((sSum / cSum) * 100) / 100 : null;
  };

  const setWeightedGradesFor = (studentId, list) => {
    setGrid(prev => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        weighted_grades: list,
        // Auto-set the subject score from the weighted computation
        score: computeWeightedAverage(list),
      },
    }));
  };

  const addWeightedRow = (studentId) => {
    const cur = grid[studentId]?.weighted_grades || [];
    setWeightedGradesFor(studentId, [...cur, { name: `Note ${cur.length + 1}`, score: '', coef: 1 }]);
  };

  const removeWeightedRow = (studentId, idx) => {
    const cur = grid[studentId]?.weighted_grades || [];
    setWeightedGradesFor(studentId, cur.filter((_, i) => i !== idx));
  };

  const updateWeightedRow = (studentId, idx, patch) => {
    if (patch.score !== undefined && patch.score !== '' && patch.score !== null) {
      const n = Number(patch.score);
      if (!Number.isNaN(n) && n > 20) {
        patch = { ...patch, score: 20 };
        toast.warning('Note plafonnée à 20');
      }
    }
    const cur = grid[studentId]?.weighted_grades || [];
    const next = cur.map((w, i) => i === idx ? { ...w, ...patch } : w);
    setWeightedGradesFor(studentId, next);
  };

  const handleSave = async () => {
    if (!selectedClass || !selectedPeriod || !selectedSubject) return;
    setSaving(true);
    try {
      const isWeighted = computed.grading_mode === 'quantitative_weighted';
      const isTotal = computed.grading_mode === 'quantitative_total';
      const isQual = computed.grading_mode === 'qualitative';

      const grades = Object.entries(grid).map(([student_id, v]) => {
        const out = { student_id };
        if (isWeighted) {
          if (v.score !== null && v.score !== undefined && !Number.isNaN(v.score)) out.score = v.score;
          // Send weighted_grades if any (backend will recompute score)
          const wgs = (v.weighted_grades || [])
            .filter(w => w.score !== null && w.score !== '' && !Number.isNaN(Number(w.score)) && Number(w.coef) > 0)
            .map(w => ({ name: w.name || '', score: Number(w.score), coef: Number(w.coef) }));
          if (wgs.length) out.weighted_grades = wgs;
        } else if (isTotal) {
          const subs = Object.fromEntries(
            Object.entries(v.sub_scores || {}).filter(([, x]) => x !== null && x !== undefined && !Number.isNaN(x))
          );
          if (Object.keys(subs).length) {
            out.sub_scores = subs;
          } else if (v.score !== null && v.score !== undefined && v.score !== '' && !Number.isNaN(Number(v.score))) {
            out.score = Number(v.score);
          }
        } else if (isQual) {
          const subs = Object.fromEntries(
            Object.entries(v.sub_appreciations || {}).filter(([, x]) => x)
          );
          if (Object.keys(subs).length) out.sub_appreciations = subs;
          const notes = Object.fromEntries(
            Object.entries(v.sub_notes || {}).filter(([, x]) => x && x.trim())
          );
          if (Object.keys(notes).length) out.sub_notes = notes;
        }
        if (v.appreciation) out.appreciation = v.appreciation;
        return out;
      }).filter(g => g.score !== undefined || g.sub_scores || g.sub_appreciations || g.sub_notes || g.appreciation || g.weighted_grades);

      if (!grades.length) {
        toast.info('Aucune note saisie');
        return;
      }
      const { data } = await gradesApi.bulkUpsert({
        class_name: selectedClass,
        school_year: computed.school_year,
        period_key: selectedPeriod,
        subject: selectedSubject,
        grades,
      });
      toast.success(`${data.upserted} notes enregistrées`);
      await loadComputed();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur sauvegarde');
    } finally { setSaving(false); }
  };

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const handleDownloadOne = async (st) => {
    try {
      const res = await gradesApi.bulletinPdf(
        st.student_id, selectedClass, selectedPeriod, computed?.school_year,
      );
      downloadBlob(
        new Blob([res.data], { type: 'application/pdf' }),
        `bulletin_${st.last_name}_${st.first_name}_${selectedPeriod}.pdf`.replace(/\s+/g, '_'),
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Échec téléchargement');
    }
  };

  const handleDownloadClass = async () => {
    try {
      const res = await gradesApi.bulletinsClassPdf(
        selectedClass, selectedPeriod, computed?.school_year,
      );
      downloadBlob(
        new Blob([res.data], { type: 'application/pdf' }),
        `bulletins_${selectedClass}_${selectedPeriod}.pdf`.replace(/\s+/g, '_'),
      );
      toast.success('Bulletins de classe téléchargés');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Échec téléchargement');
    }
  };

  const toggleLock = async () => {
    if (!selectedClass || !selectedPeriod || !computed) return;
    try {
      if (computed.is_locked) {
        await gradesApi.unlockPeriod({
          class_name: selectedClass,
          school_year: computed.school_year,
          period_key: selectedPeriod,
        });
        toast.success('Période déverrouillée');
      } else {
        await gradesApi.lockPeriod({
          class_name: selectedClass,
          school_year: computed.school_year,
          period_key: selectedPeriod,
        });
        toast.success('Période verrouillée — saisies désactivées');
      }
      await loadComputed();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur verrouillage');
    }
  };

  const isLocked = !!computed?.is_locked;
  const canLock = ['admin', 'director_studies'].includes(user?.role);
  const canUnlock = user?.role === 'admin';
  const isAppreciationSubject = subjectMeta?.type === 'appreciation';
  const isExcludedSubject = !!subjectMeta?.excluded_from_total;
  const needsDirectScore = computed?.grading_mode === 'quantitative_total'
    && !isAppreciationSubject && subActivities.length === 0;

  // ----- UI -----
  return (
    <div className="space-y-6" data-testid="grades-page">
      <h1 className="font-outfit text-2xl font-bold text-[#0F172A] flex items-center gap-2">
        <ChalkboardTeacher size={24} className="text-[#0052FF]" weight="duotone" />
        Saisie des notes
      </h1>

      {/* Selectors */}
      <Card className="border border-[#E2E8F0] shadow-none">
        <CardContent className="pt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label className="mb-1.5">Classe</Label>
            <Select value={selectedClass} onValueChange={setSelectedClass}>
              <SelectTrigger className="border-[#E2E8F0]" data-testid="grades-class">
                <SelectValue placeholder="Sélectionner…" />
              </SelectTrigger>
              <SelectContent>
                {classes.map(c => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="mb-1.5">
              Période
              {classCfg && <span className="ml-2 text-xs text-[#64748B]">({classCfg.period_type})</span>}
            </Label>
            <Select value={selectedPeriod} onValueChange={setSelectedPeriod} disabled={!classCfg}>
              <SelectTrigger className="border-[#E2E8F0]" data-testid="grades-period">
                <SelectValue placeholder={classCfg ? 'Sélectionner…' : 'Choisir une classe d\'abord'} />
              </SelectTrigger>
              <SelectContent>
                {periodOptions.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="mb-1.5">Discipline</Label>
            <Select value={selectedSubject} onValueChange={setSelectedSubject} disabled={!computed}>
              <SelectTrigger className="border-[#E2E8F0]" data-testid="grades-subject">
                <SelectValue placeholder="Sélectionner…" />
              </SelectTrigger>
              <SelectContent>
                {computed?.subjects.map(s => <SelectItem key={s.name} value={s.name}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Class stats */}
      {computed && computed.class_stats?.count_with_grades > 0 && (
        <Card className="border border-emerald-200 bg-emerald-50/30 shadow-none" data-testid="grades-class-stats">
          <CardContent className="pt-6 grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
            <Stat label="Moyenne classe" value={`${computed.class_stats.avg}/20`} />
            <Stat label="Plus haute" value={`${computed.class_stats.top}/20`} />
            <Stat label="Plus basse" value={`${computed.class_stats.bottom}/20`} />
            <Stat label="Notés / Total" value={`${computed.class_stats.count_with_grades} / ${computed.class_stats.count_total}`} />
            <Stat label="Modèle" value={computed.template_label} />
          </CardContent>
        </Card>
      )}

      {/* Grade entry grid */}
      {computed && selectedSubject && (
        <Card className="border border-[#E2E8F0] shadow-none">
          <CardHeader className="pb-3 flex flex-row items-center justify-between">
            <CardTitle className="font-outfit text-base flex items-center gap-2">
              {selectedSubject}
              {subjectMeta?.max_score && <span className="ml-2 text-sm font-manrope text-[#64748B]">/{subjectMeta.max_score}</span>}
              {isExcludedSubject && (
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-100 text-amber-800" data-testid="excluded-badge">
                  Hors moyenne
                </span>
              )}
              {isAppreciationSubject && (
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 text-slate-700" data-testid="appreciation-badge">
                  Appréciation
                </span>
              )}
              {subjectMeta?.default_coef && (
                <span className="ml-2 text-sm font-manrope text-[#64748B]">
                  · coef {computed.coefficients?.[selectedSubject] ?? subjectMeta.default_coef}
                </span>
              )}
              {isLocked && (
                <span className="ml-3 inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700"
                      data-testid="grades-locked-badge">
                  <Lock size={12} weight="fill" />
                  Verrouillée
                </span>
              )}
            </CardTitle>
            <div className="flex items-center gap-2">
              {canLock && (
                <Button
                  variant="outline" size="sm"
                  onClick={toggleLock}
                  disabled={isLocked && !canUnlock}
                  title={isLocked
                    ? (canUnlock ? 'Déverrouiller (admin)' : 'Seul un admin peut déverrouiller')
                    : 'Verrouiller la période'}
                  className={isLocked
                    ? 'border-red-200 text-red-700 hover:bg-red-50'
                    : 'border-[#0F172A] text-[#0F172A] hover:bg-slate-50'}
                  data-testid="grades-lock-toggle"
                >
                  {isLocked ? <LockOpen size={14} className="mr-2" /> : <Lock size={14} className="mr-2" />}
                  {isLocked ? 'Déverrouiller' : 'Verrouiller'}
                </Button>
              )}
              <Button onClick={handleSave} disabled={saving || isLocked} className="bg-[#0052FF] hover:bg-[#0040CC]" data-testid="grades-save">
                <FloppyDisk size={16} className="mr-2" />
                {saving ? '…' : 'Enregistrer'}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Élève</TableHead>
                  {computed.grading_mode === 'quantitative_weighted' && (
                    <TableHead className="text-center">Note /20</TableHead>
                  )}
                  {needsDirectScore && (
                    <TableHead className="text-center">Note{subjectMeta?.max_score ? ` /${subjectMeta.max_score}` : ''}</TableHead>
                  )}
                  {subActivities.map(sa => (
                    <TableHead key={sa.name} className="text-center text-xs">
                      {sa.name}
                      {sa.max_score && <span className="block text-[10px] text-[#94A3B8]">/{sa.max_score}</span>}
                    </TableHead>
                  ))}
                  {computed.grading_mode === 'qualitative' && (
                    <TableHead className="text-center text-xs">Bilan</TableHead>
                  )}
                  <TableHead>Appréciation</TableHead>
                  {computed.grading_mode === 'qualitative' && (
                    <TableHead className="text-center text-xs">Progression</TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {computed.students.map((st) => {
                  const row = grid[st.student_id] || {};
                  return (
                    <TableRow key={st.student_id} data-testid={`grades-row-${st.student_id}`}>
                      <TableCell className="font-manrope text-sm">
                        {st.last_name} {st.first_name}
                      </TableCell>
                      {computed.grading_mode === 'quantitative_weighted' && (() => {
                        const wgs = row.weighted_grades || [];
                        const hasWg = wgs.length > 0;
                        return (
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Input
                                type="number" min="0" max="20" step="0.25"
                                value={row.score ?? ''}
                                onChange={(e) => updateScore(st.student_id, e.target.value)}
                                disabled={hasWg || isLocked}
                                className={`h-9 w-20 text-center ${hasWg ? 'bg-slate-100 text-[#0F172A] font-semibold cursor-not-allowed' : ''}`}
                                data-testid={`score-${st.student_id}`}
                                title={hasWg ? 'Calculée automatiquement depuis les notes détaillées' : ''}
                              />
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => setWgDialog({ open: true, studentId: st.student_id })}
                                disabled={isLocked}
                                title="Ajouter/éditer les notes pondérées"
                                className={`h-9 px-2 ${hasWg ? 'border-[#0052FF] text-[#0052FF]' : 'border-[#CBD5E1] text-[#64748B]'}`}
                                data-testid={`weighted-grades-btn-${st.student_id}`}
                              >
                                <Calculator size={14} className="mr-1" />
                                Détail{hasWg ? ` (${wgs.length})` : ''}
                              </Button>
                            </div>
                          </TableCell>
                        );
                      })()}
                      {needsDirectScore && (
                        <TableCell>
                          <Input
                            type="number" min="0" max={subjectMeta?.max_score || undefined} step="0.5"
                            value={row.score ?? ''}
                            onChange={(e) => updateScore(st.student_id, e.target.value)}
                            disabled={isLocked}
                            className="h-9 w-20 text-center"
                            data-testid={`score-${st.student_id}`}
                          />
                        </TableCell>
                      )}
                      {subActivities.map(sa => (
                        <TableCell key={sa.name}>
                          {computed.grading_mode === 'qualitative' ? (
                            <div className="flex flex-col gap-1 min-w-[150px]">
                              <Select
                                value={row.sub_appreciations?.[sa.name] || 'none'}
                                onValueChange={(v) => updateAppreciation(st.student_id, v === 'none' ? '' : v, sa.name)}
                                disabled={isLocked}
                              >
                                <SelectTrigger className="h-9 text-xs" data-testid={`smiley-${st.student_id}-${sa.name}`}>
                                  <SelectValue placeholder="—" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">— Non évalué</SelectItem>
                                  {SMILEY_LEVELS.map(l => (
                                    <SelectItem key={l.level} value={String(l.level)}>
                                      <span style={{ color: l.color }}>{l.emoji} {l.label}</span>
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <Input
                                value={row.sub_notes?.[sa.name] || ''}
                                onChange={(e) => updateSubNote(st.student_id, sa.name, e.target.value)}
                                placeholder="Note (optionnel)"
                                disabled={isLocked}
                                className="h-7 text-[11px]"
                                data-testid={`smiley-note-${st.student_id}-${sa.name}`}
                              />
                            </div>
                          ) : (
                            <Input
                              type="number" min="0" max={sa.max_score || undefined} step="0.5"
                              value={row.sub_scores?.[sa.name] ?? ''}
                              onChange={(e) => updateSub(st.student_id, sa.name, e.target.value)}
                              className="h-9 w-16 text-center"
                              data-testid={`sub-${st.student_id}-${sa.name}`}
                            />
                          )}
                        </TableCell>
                      ))}
                      {computed.grading_mode === 'qualitative' && (() => {
                        const lvl = bilanFromLevels(subActivities.map(sa => row.sub_appreciations?.[sa.name]));
                        const m = lvl ? SMILEY_BY_LEVEL[lvl] : null;
                        return (
                          <TableCell className="text-center" data-testid={`bilan-${st.student_id}`}>
                            {m ? (
                              <span className="text-sm font-semibold whitespace-nowrap" style={{ color: m.color }}>
                                {m.emoji} {m.label}
                              </span>
                            ) : <span className="text-[#CBD5E1]">—</span>}
                          </TableCell>
                        );
                      })()}
                      <TableCell>
                        <Textarea
                          rows={1}
                          value={row.appreciation || ''}
                          onChange={(e) => updateAppreciation(st.student_id, e.target.value)}
                          placeholder="Observation…"
                          className="min-h-[36px] text-xs"
                          data-testid={`grade-appreciation-${st.student_id}`}
                        />
                      </TableCell>
                      {computed.grading_mode === 'qualitative' && (
                        <TableCell className="text-center">
                          <Button
                            variant="outline" size="sm"
                            className="h-8 px-2 border-[#CBD5E1] text-[#0052FF]"
                            onClick={() => setProgression({ open: true, studentId: st.student_id, studentName: `${st.last_name} ${st.first_name}`, data: null, loading: true })}
                            data-testid={`progression-btn-${st.student_id}`}
                          >
                            <TrendUp size={14} className="mr-1" /> Voir
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Per-student summary (read-only) */}
      {computed && computed.students.some(s => s.general_average !== null) && (
        <Card className="border border-[#E2E8F0] shadow-none">
          <CardHeader className="pb-3 flex flex-row items-center justify-between">
            <CardTitle className="font-outfit text-base flex items-center gap-2">
              <Trophy size={18} className="text-amber-500" weight="duotone" />
              Classement & moyennes générales
            </CardTitle>
            <Button
              variant="outline" onClick={handleDownloadClass}
              className="border-[#B45309] text-[#B45309] hover:bg-[#FEF3C7]"
              data-testid="bulletins-class-pdf"
            >
              <FilesIcon size={16} className="mr-2" />
              Exporter tous les bulletins
            </Button>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16 text-center">Rang</TableHead>
                  <TableHead>Élève</TableHead>
                  <TableHead className="text-center">Moyenne /20</TableHead>
                  {computed.grading_mode === 'quantitative_total' && (
                    <TableHead className="text-center">Total brut</TableHead>
                  )}
                  <TableHead className="text-center">Rang Fr.</TableHead>
                  <TableHead className="text-center">Rang Maths</TableHead>
                  <TableHead>Appréciation auto.</TableHead>
                  <TableHead className="text-right w-12">PDF</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {computed.students
                  .filter(s => s.general_average !== null)
                  .sort((a, b) => (a.general_rank || 999) - (b.general_rank || 999))
                  .map(s => (
                    <TableRow key={s.student_id}>
                      <TableCell className="text-center font-outfit font-bold">{s.general_rank}</TableCell>
                      <TableCell>{s.last_name} {s.first_name}</TableCell>
                      <TableCell className="text-center font-semibold">{s.general_average}</TableCell>
                      {computed.grading_mode === 'quantitative_total' && (
                        <TableCell className="text-center text-xs text-[#64748B]">
                          {s.raw_total}/{s.max_total}
                        </TableCell>
                      )}
                      <TableCell className="text-center text-xs">{s.subject_ranks?.['Français'] || '—'}</TableCell>
                      <TableCell className="text-center text-xs">{s.subject_ranks?.['Mathématiques'] || '—'}</TableCell>
                      <TableCell className="text-xs text-[#64748B]">{APPRECIATION_BY_AVG(s.general_average)}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm" variant="ghost"
                          onClick={() => handleDownloadOne(s)}
                          title="Télécharger le bulletin PDF"
                          className="text-[#B45309] hover:bg-[#FEF3C7]"
                          data-testid={`pdf-bulletin-${s.student_id}`}
                        >
                          <FilePdf size={16} />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {loading && (
        <div className="flex items-center justify-center py-8 text-[#64748B]">
          <ArrowsClockwise size={20} className="animate-spin mr-2" />
          Chargement…
        </div>
      )}

      {!loading && computed && computed.students.length === 0 && (
        <div className="text-center py-8 text-[#64748B] flex flex-col items-center gap-2">
          <ChartLine size={32} className="text-[#CBD5E1]" />
          Aucun élève inscrit dans cette classe.
        </div>
      )}

      {/* ===== Annual summary card (visible when at least one period has data for this year) ===== */}
      {annual && annual.class_stats?.count > 0 && (
        <Card className="border-2 border-amber-300 bg-amber-50/40 shadow-none" data-testid="grades-annual-card">
          <CardHeader className="pb-3">
            <CardTitle className="font-outfit text-base flex items-center gap-2">
              <Calendar size={18} className="text-amber-700" weight="duotone" />
              Bilan annuel — {computed?.school_year || annual.school_year}
              {annual.last_period && (
                <span className="ml-2 text-xs font-manrope text-[#B45309] bg-amber-100 px-2 py-0.5 rounded-full">
                  Publié sur la {annual.last_period}
                </span>
              )}
            </CardTitle>
            <p className="text-xs text-[#78350F] mt-1">
              {annual.grading_mode === 'quantitative_weighted' && annual.period_type === 'trimestre'
                ? 'Formule secondaire : (T1×1 + T2×2 + T3×2) / 5'
                : 'Formule : moyenne des périodes disponibles'}
              {' · '}
              Périodes prises en compte : {(annual.periods_present || []).join(', ') || '—'}
            </p>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 px-4 pt-2 pb-3 text-sm border-b border-amber-200">
              <Stat label="Moyenne classe (annuelle)" value={`${annual.class_stats.annual_avg ?? '—'}/20`} />
              <Stat label="Plus haute (année)" value={`${annual.class_stats.top ?? '—'}/20`} />
              <Stat label="Plus basse (année)" value={`${annual.class_stats.bottom ?? '—'}/20`} />
              <Stat label="Élèves classés" value={`${annual.class_stats.count}`} />
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16 text-center">Rang annuel</TableHead>
                  <TableHead>Élève</TableHead>
                  {(annual.periods_present || []).map(p => (
                    <TableHead key={p} className="text-center text-xs">{p}</TableHead>
                  ))}
                  <TableHead className="text-center">Moyenne annuelle</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {annual.students
                  .filter(s => s.annual_average !== null)
                  .sort((a, b) => (a.annual_rank || 999) - (b.annual_rank || 999))
                  .map(s => (
                    <TableRow key={s.student_id} data-testid={`annual-row-${s.student_id}`}>
                      <TableCell className="text-center font-outfit font-bold text-amber-800">{s.annual_rank}</TableCell>
                      <TableCell>{s.last_name} {s.first_name}</TableCell>
                      {(annual.periods_present || []).map(p => (
                        <TableCell key={p} className="text-center text-xs">
                          {s.periods?.[p] != null ? `${s.periods[p]}` : '—'}
                        </TableCell>
                      ))}
                      <TableCell className={`text-center font-bold ${s.annual_average >= 10 ? 'text-emerald-700' : 'text-red-600'}`}>
                        {s.annual_average}/20
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* ===== Weighted grades editor Dialog (secondaire) ===== */}
      <WeightedGradesDialog
        open={wgDialog.open}
        onClose={() => setWgDialog({ open: false, studentId: null })}
        student={computed?.students?.find(s => s.student_id === wgDialog.studentId) || null}
        subject={selectedSubject}
        row={grid[wgDialog.studentId] || {}}
        onAdd={() => addWeightedRow(wgDialog.studentId)}
        onRemove={(idx) => removeWeightedRow(wgDialog.studentId, idx)}
        onUpdate={(idx, patch) => updateWeightedRow(wgDialog.studentId, idx, patch)}
        computedAverage={computeWeightedAverage(grid[wgDialog.studentId]?.weighted_grades)}
        isLocked={isLocked}
      />

      <ProgressionDialog
        state={progression}
        onClose={() => setProgression({ open: false, studentId: null, studentName: '', data: null, loading: false })}
      />
    </div>
  );
}

const periodLabel = (key) => {
  const m = String(key).match(/^(periode|examen)-(\d+)$/);
  if (!m) return key;
  const n = parseInt(m[2], 10);
  if (m[1] === 'periode') return `${n === 1 ? '1ère' : n + 'ème'} Période`;
  return `${n === 1 ? '1er' : n + 'ème'} Examen`;
};

const ProgressionDialog = ({ state, onClose }) => {
  const data = state.data;
  return (
    <Dialog open={state.open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl max-h-[88vh] overflow-y-auto" data-testid="progression-dialog">
        <DialogHeader>
          <DialogTitle className="font-outfit flex items-center gap-2">
            <TrendUp size={20} className="text-[#0052FF]" /> Progression — {state.studentName}
          </DialogTitle>
        </DialogHeader>
        {!data ? (
          <p className="py-8 text-center text-sm text-[#94A3B8]">Chargement…</p>
        ) : data.periods.length === 0 ? (
          <p className="py-8 text-center text-sm text-[#94A3B8]">Aucune évaluation saisie pour le moment.</p>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3 rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] p-3" data-testid="annual-bilan">
              <div>
                <p className="text-xs uppercase tracking-wide text-[#64748B] font-semibold">Bilan global annuel</p>
                <p className="text-[11px] text-[#94A3B8]">Toutes disciplines confondues, sur l'année</p>
              </div>
              {(() => {
                const m = data.annual_bilan ? SMILEY_BY_LEVEL[data.annual_bilan] : null;
                const TI = data.annual_trend === 'up' ? TrendUp : data.annual_trend === 'down' ? TrendDown : ArrowRight;
                const tc = data.annual_trend === 'up' ? '#16A34A' : data.annual_trend === 'down' ? '#DC2626' : '#94A3B8';
                return m ? (
                  <div className="flex items-center gap-2">
                    <span className="text-xl font-bold whitespace-nowrap" style={{ color: m.color }}>{m.emoji} {m.label}</span>
                    <TI size={22} weight="bold" color={tc} />
                  </div>
                ) : <span className="text-[#CBD5E1]">Pas encore évalué</span>;
              })()}
            </div>
            <p className="text-xs text-[#64748B]">
              Évolution du niveau d'acquisition de la première à la dernière évaluation
              (😟 À renforcer · 😐 En cours · 🙂 Acquis · 😄 Maîtrisé).
            </p>
            <div className="overflow-x-auto rounded-lg border border-[#E2E8F0]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[180px]">Discipline / Activité</TableHead>
                    {data.periods.map(p => (
                      <TableHead key={p} className="text-center text-[11px] whitespace-nowrap">{periodLabel(p)}</TableHead>
                    ))}
                    <TableHead className="text-center">Tendance</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow className="bg-[#EFF6FF]">
                    <TableCell className="font-bold text-sm text-[#0052FF]">Bilan global (toutes disciplines)</TableCell>
                    {data.periods.map(p => {
                      const m = data.annual_by_period?.[p] ? SMILEY_BY_LEVEL[data.annual_by_period[p]] : null;
                      return <TableCell key={p} className="text-center text-lg" title={m?.label || ''}>{m ? m.emoji : '·'}</TableCell>;
                    })}
                    <TableCell />
                  </TableRow>
                  {data.subjects.map(subj => {
                    const TrendIcon = subj.trend === 'up' ? TrendUp : subj.trend === 'down' ? TrendDown : ArrowRight;
                    const tColor = subj.trend === 'up' ? '#16A34A' : subj.trend === 'down' ? '#DC2626' : '#94A3B8';
                    return (
                      <React.Fragment key={subj.name}>
                        <TableRow className="bg-[#F1F5F9]">
                          <TableCell className="font-semibold text-sm">{subj.name} <span className="text-[10px] text-[#64748B]">(Bilan)</span></TableCell>
                          {data.periods.map(p => {
                            const m = subj.bilan[p] ? SMILEY_BY_LEVEL[subj.bilan[p]] : null;
                            return <TableCell key={p} className="text-center text-lg" title={m?.label || ''}>{m ? m.emoji : '·'}</TableCell>;
                          })}
                          <TableCell className="text-center"><TrendIcon size={18} weight="bold" color={tColor} /></TableCell>
                        </TableRow>
                        {subj.activities.map(act => (
                          <TableRow key={subj.name + act.name}>
                            <TableCell className="pl-6 text-xs text-[#475569]">{act.name}</TableCell>
                            {data.periods.map(p => {
                              const m = act.levels[p] ? SMILEY_BY_LEVEL[act.levels[p]] : null;
                              return <TableCell key={p} className="text-center" title={m?.label || ''}>{m ? m.emoji : <span className="text-[#CBD5E1]">·</span>}</TableCell>;
                            })}
                            <TableCell />
                          </TableRow>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="progression-close">Fermer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

function WeightedGradesDialog({ open, onClose, student, subject, row, onAdd, onRemove, onUpdate, computedAverage, isLocked }) {
  if (!student) return null;
  const wgs = row?.weighted_grades || [];
  const totalCoef = wgs.reduce((acc, w) => acc + (Number(w.coef) || 0), 0);
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="weighted-grades-dialog">
        <DialogHeader>
          <DialogTitle className="font-outfit">
            Notes détaillées — {student.last_name} {student.first_name}
            <span className="ml-2 text-sm text-[#64748B] font-manrope">/ {subject}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <p className="text-xs text-[#64748B]">
            Ajoutez plusieurs notes avec leur coefficient de pondération.
            La moyenne de la matière est calculée automatiquement : <b>Σ(note × coef) / Σ(coef)</b>.
          </p>

          {wgs.length === 0 ? (
            <div className="text-center py-6 text-sm text-[#94A3B8] border-2 border-dashed border-[#E2E8F0] rounded">
              Aucune note détaillée. Cliquez sur « Ajouter une note ».
            </div>
          ) : (
            <div className="border border-[#E2E8F0] rounded overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[45%]">Libellé</TableHead>
                    <TableHead className="text-center">Note /20</TableHead>
                    <TableHead className="text-center">Coef.</TableHead>
                    <TableHead className="text-center">Pondérée</TableHead>
                    <TableHead className="w-8"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {wgs.map((w, i) => {
                    const weighted = (Number(w.score) || 0) * (Number(w.coef) || 0);
                    const validCell = w.score !== '' && w.score !== null && !Number.isNaN(Number(w.score));
                    return (
                      <TableRow key={i} data-testid={`wg-row-${i}`}>
                        <TableCell>
                          <Input
                            value={w.name || ''}
                            onChange={(e) => onUpdate(i, { name: e.target.value })}
                            placeholder="Devoir 1, Interrogation, Composition…"
                            className="h-9 text-sm"
                            disabled={isLocked}
                            data-testid={`wg-name-${i}`}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number" min="0" max="20" step="0.25"
                            value={w.score ?? ''}
                            onChange={(e) => onUpdate(i, { score: e.target.value })}
                            className="h-9 w-24 text-center mx-auto"
                            disabled={isLocked}
                            data-testid={`wg-score-${i}`}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number" min="0.5" step="0.5"
                            value={w.coef ?? 1}
                            onChange={(e) => onUpdate(i, { coef: e.target.value })}
                            className="h-9 w-20 text-center mx-auto"
                            disabled={isLocked}
                            data-testid={`wg-coef-${i}`}
                          />
                        </TableCell>
                        <TableCell className="text-center text-xs text-[#64748B]">
                          {validCell && Number(w.coef) > 0 ? weighted.toFixed(2) : '—'}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost" size="sm"
                            onClick={() => onRemove(i)}
                            disabled={isLocked}
                            className="h-8 w-8 p-0 text-red-500 hover:bg-red-50"
                            data-testid={`wg-remove-${i}`}
                          >
                            <Trash size={14} />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}

          <div className="flex items-center justify-between pt-2 border-t border-[#E2E8F0]">
            <div className="text-sm">
              <span className="text-[#64748B]">Total coef.</span>{' '}
              <span className="font-outfit font-bold">{totalCoef}</span>
            </div>
            <div className="text-lg font-outfit font-bold text-[#0052FF]" data-testid="wg-computed-average">
              Moyenne matière : {computedAverage !== null ? `${computedAverage}/20` : '—'}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onAdd} disabled={isLocked} data-testid="wg-add-btn">
            <Plus size={14} className="mr-1" /> Ajouter une note
          </Button>
          <Button onClick={onClose} className="bg-[#0052FF] hover:bg-[#0040CC]" data-testid="wg-close-btn">
            Fermer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value }) {
  return (
    <div className="text-center">
      <p className="text-[10px] uppercase text-emerald-700/70 tracking-wider">{label}</p>
      <p className="font-outfit font-bold text-emerald-800 mt-1">{value}</p>
    </div>
  );
}
